import { useActionConfirmation } from '../components/ActionFeedbackProvider';
// React & Hooks
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Icons
import { Home, Plus, Trash, Pencil, ChevronLeft, ChevronRight, Weight } from "lucide-react";

// API client
import api from "../lib/api";

// Modals
import AddYieldModal from "../components/modals/AddYieldModal";
import EditYieldModal from "../components/modals/EditYieldModal";
import PageHeading from "../components/PageHeading";

// i18n
import { useTranslation } from "react-i18next";

const YieldScreen = () => {
  // States
  const [yields, setYields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showYieldModal, setShowYieldModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedYield, setSelectedYield] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date().toISOString().split("T")[0];
    return today;
  });

  // Hooks
  const navigate = useNavigate();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Base button style with Tailwind dark: classes
  const baseBtn =
    "inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold shadow transition " +
    "bg-black text-white hover:bg-white hover:text-black " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  // Fetch yields when selectedDate changes
  useEffect(() => {
    fetchYields();
  }, [selectedDate]);

  // Fetch yields from API
  const fetchYields = async () => {
    setLoading(true);
    try {
      const res = await api.get("/yields", { params: { date: selectedDate } });
      setYields(res.data);
    } catch (err) {
      console.error("Error fetching yields:", err);
    } finally {
      setLoading(false);
    }
  };

  // Delete yield
  const handleDelete = async (id) => {
    const confirmDelete = await confirmAction(t("confirmDeleteYield"));
    if (!confirmDelete) return;
    try {
      await api.delete(`/yields/${id}`);
      fetchYields();
    } catch (err) {
      console.error("Error deleting yield:", err);
    }
  };

  // Shift calendar date
  const shiftDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate.toISOString().split("T")[0]);
  };

  // Get weekday name from i18n
  const getDayName = (dateStr) => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const dayIndex = date.getDay();
    return t(`days.${dayIndex}`);
  };

  // Render screen
  return (
    <div className="min-h-screen p-6">
      {/* Home button */}
      <div className="flex justify-center mb-6">
        <button className={baseBtn} onClick={() => navigate("/")}>
          <Home size={18} />
          {t("home")}
        </button>
      </div>

      <PageHeading icon={<Weight size={26} />} title={t("yields")} />

      {/* Calendar bar */}
      <div className="flex items-center justify-center gap-4 mb-4 w-full">
        <button
          className={`${baseBtn} !px-2 !py-2 rounded-full`}
          onClick={() => shiftDate(-1)}
        >
          <ChevronLeft size={20} />
        </button>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm shadow w-48 text-center
                     bg-white text-black
                     dark:bg-gray-900 dark:text-white dark:border-gray-600
                     [color-scheme:light] dark:[color-scheme:dark]"
        />
        <button
          className={`${baseBtn} !px-2 !py-2 rounded-full`}
          onClick={() => shiftDate(1)}
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Day name */}
      <h1 className="text-3xl font-bold text-center mb-6">
        {getDayName(selectedDate)}
      </h1>

      {/* Add Yield button */}
      <div className="flex justify-center mb-6">
        <button className={baseBtn} onClick={() => setShowYieldModal(true)}>
          <Plus size={18} />
          {t("addYield")}
        </button>
      </div>

      {/* Yields list */}
      {loading ? (
        <p className="text-center">{t("loading")}</p>
      ) : yields.length === 0 ? (
        <p className="text-center">{t("noYields")}</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {yields.map((y) => (
            <div
              key={y.id}
              className="p-4 rounded-lg shadow bg-white/90 dark:bg-gray-800/90 flex flex-col justify-between"
            >
              <div>
                {/* Product name */}
                <h3 className="text-lg font-bold text-center mb-2">
                  {y.productName || "-"}
                </h3>
                <p className="text-xs text-center italic mb-2">
                  {t("mode")}: {t(y.mode)}
                </p>

                {y.mode === "fixed" ? (
                  <>
                    <p className="text-xs">
                      {t("baseWeight")}: {y.baseWeight.toFixed(2)} KG
                    </p>
                    <p className="text-xs">{t("boxes")}: {y.boxes}</p>
                  </>
                ) : (
                  <>
                    <p className="text-xs font-semibold">{t("boxWeights")}:</p>
                    <ul className="text-xs list-disc list-inside">
                      {y.boxWeights?.map((bw, i) => (
                        <li key={i}>{bw.toFixed(2)} KG</li>
                      ))}
                    </ul>
                  </>
                )}

                <p className="text-xs font-semibold mt-2">{t("barrels")}:</p>
                <ul className="text-xs list-disc list-inside">
                  {y.barrels?.map((b, i) => (
                    <li key={i}>{b.toFixed(2)} KG</li>
                  ))}
                </ul>

                <hr className="my-2" />
                <p className="text-xs">{t("totalBase")}: {y.totalBase.toFixed(2)} KG</p>
                <p className="text-xs">{t("totalObtained")}: {y.totalObtained.toFixed(2)} KG</p>
                <p className="text-xs">{t("weightDifference")}: {y.weightDifference.toFixed(2)} KG</p>
                <p className="text-xs">{t("yieldRatio")}: {y.yieldRatio.toFixed(3)}</p>
              </div>

              {/* Action buttons */}
              <div className="flex gap-1 mt-3 justify-center">
                <button
                  className={baseBtn}
                  onClick={() => {
                    setSelectedYield(y);
                    setShowEditModal(true);
                  }}
                >
                  <Pencil size={14} />
                </button>
                <button
                  className={baseBtn}
                  onClick={() => handleDelete(y.id)}
                >
                  <Trash size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showYieldModal && (
        <AddYieldModal
          isOpen={showYieldModal}
          onClose={() => setShowYieldModal(false)}
          onSaved={fetchYields}
          selectedDate={selectedDate}
        />
      )}

      {showEditModal && selectedYield && (
        <EditYieldModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSaved={fetchYields}
          yieldData={selectedYield}
        />
      )}
    </div>
  );
};

export default YieldScreen;
